import { useRef, useState, useEffect } from 'react';
import {
  AccommodationRoom,
  Option,
  EnumOptionAccommodationSaleType,
  OrderItemAccommodationRoom,
  AvailableDate,
  EnumAccommodationRoomType,
} from 'src/types';
import UserIcon from 'public/images/experience/icon_user.png';
import ShowerIcon from 'public/images/experience/icon_shower.png';
import BedIcon from 'public/images/experience/icon_bed.png';
import { Carousel } from 'react-bootstrap';
import { FiChevronRight, FiChevronLeft, FiX } from 'react-icons/fi';
import LightBox from 'src/components/LightBox';
import ImageWithFallback from 'src/components/ImageWithFallback';
import pluralize from 'pluralize';
import Money from 'src/components/Money';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import { sortBy, flatten, truncate } from 'lodash-es';
import { Modal } from 'react-bootstrap';
import Slider from 'react-slick';
import { displayPriceUnit } from 'src/helpers';
import { commonCarouselSettings, ALPHABET_MAP, DEFAULT_IMAGE } from 'src/lib/constants';
import classNames from 'classnames';
import styles from 'src/styles/modules/components/option_accommodation_room_carousel.module.scss';
import useModalAccommodationRoomContext from 'src/hooks/useModalAccommodationRoomContext';
import CheckInCircleSvg from 'public/images/svg_icons/check_in_circle.svg';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';

type PickAccommodationRoomCardProps = {
  isGiftExperience?: boolean;
  accommodationRoom: AccommodationRoom;
  option: Option;
  setViewAccommodationRoomId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  qty?: number;
};

const PickAccommodationRoomCard: React.FC<PickAccommodationRoomCardProps> = ({
  isGiftExperience,
  accommodationRoom,
  option,
  setViewAccommodationRoomId,
  setShowModal,
  qty,
}) => {
  const currentCurrency = useCurrentCurrency();
  const isSoldOut = typeof qty === 'number' && qty === 0;
  return (
    <div
      className={classNames('tw-border tw-rounded-lg tw-cursor-pointer tw-relative', {
        'tw-border-brand-green': !isSoldOut,
        'tw-border-grey-lighter': isSoldOut,
      })}
    >
      <Carousel
        controls={option.images.length > 1}
        prevIcon={
          accommodationRoom.images.length > 1 ? (
            <div className={styles.carouselArrowBtn}>
              <FiChevronLeft strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          ) : null
        }
        nextIcon={
          accommodationRoom.images.length > 1 ? (
            <div className={styles.carouselArrowBtn}>
              <FiChevronRight strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          ) : null
        }
        indicators={accommodationRoom.images.length > 1}
        className={`carousel slide ${styles.carouselImages}`}
        interval={null}
        pause={false}
        keyboard={false}
        touch={false}
      >
        {accommodationRoom.images.length > 0 ? (
          accommodationRoom.images.map((image) => (
            <Carousel.Item key={image.id} className="carousel-item tw-relative">
              <ImageWithFallback
                record={image}
                imgField="webpMdUrl"
                fill
                alt=""
                className="tw-rounded-tr-lg tw-rounded-tl-lg tw-object-cover tw-cursor-pointer"
                onClick={() => {
                  setViewAccommodationRoomId(accommodationRoom.id);
                  setShowModal(true);
                }}
              />
            </Carousel.Item>
          ))
        ) : (
          <Carousel.Item className="carousel-item tw-relative">
            <ImageWithFallback
              src={DEFAULT_IMAGE}
              fill
              alt=""
              className="tw-object-cover tw-cursor-pointer tw-rounded-tr-lg tw-rounded-tl-lg"
              onClick={() => {
                setViewAccommodationRoomId(accommodationRoom.id);
                setShowModal(true);
              }}
            />
          </Carousel.Item>
        )}
      </Carousel>
      <div
        className="tw-p-3 md:tw-p-4 tw-cursor-pointer"
        onClick={() => {
          setViewAccommodationRoomId(accommodationRoom.id);
          setShowModal(true);
        }}
      >
        <div
          className={classNames(
            'tw-font-semibold tw-font-styleSans tw-text-center tw-text-lg tw-leading-none md:tw-leading-none tw-mb-2 tw-h-10',
            {
              'tw-text-brand-navy': !isSoldOut,
              'tw-text-grey': isSoldOut,
            }
          )}
        >
          {truncate(accommodationRoom.name, { length: 56 })}
        </div>

        <div className="tw-mb-2 tw-text-grey tw-text-sm tw-text-center">
          {[
            `Sleeps ${accommodationRoom.maxNumOfPerson}`,
            accommodationRoom.beds.length > 0
              ? pluralize('bed', accommodationRoom.beds.reduce((memo, bed) => memo + bed.qty, 0), true)
              : null,
            accommodationRoom.numPrivateBathroom && accommodationRoom.numPrivateBathroom > 0
              ? 'Ensuite'
              : null,
          ]
            .filter((v) => v)
            .join(' | ')}
        </div>

        <div className={classNames('tw-text-center tw-text-sm', { 'tw-text-grey': isSoldOut })}>
          From{' '}
          <Money
            hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
            amount={accommodationRoom.minAmount}
            toCurrency={currentCurrency}
            className={classNames('tw-font-semibold tw-font-styleSans tw-text-base', {
              'tw-text-grey': isSoldOut,
              'tw-text-orange': !isSoldOut,
            })}
          />
          {displayPriceUnit(
            option,
            accommodationRoom.roomType === EnumAccommodationRoomType.dormBed ? '/bed' : undefined
          )}
        </div>

        {typeof qty === 'number' ? (
          <div className="tw-mt-2 tw-text-xs tw-leading-none tw-text-center">
            {qty > 0 ? (
              <span className="tw-text-orange">
                {pluralize(
                  accommodationRoom.roomType === EnumAccommodationRoomType.dormBed ? 'bed' : 'room',
                  qty,
                  true
                )}{' '}
                left on your date
              </span>
            ) : (
              <span className="tw-text-grey">Sold out on your date</span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

type AccommodationRoomCardProps = {
  accommodationRoom: AccommodationRoom;
  setViewAccommodationRoomId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setShowLightbox: React.Dispatch<React.SetStateAction<boolean>>;
  option: Option;
  isAllNight: boolean;
};

const AccommodationRoomCard: React.FC<AccommodationRoomCardProps> = ({
  accommodationRoom,
  setViewAccommodationRoomId,
  setShowLightbox,
  option,
  isAllNight,
}) => {
  const firstImage = accommodationRoom.images[0];
  return (
    <div
      className="tw-flex-shrink-0 tw-px-2 tw-cursor-pointer"
      onClick={() => {
        setViewAccommodationRoomId(accommodationRoom.id);
        setShowLightbox(true);
      }}
    >
      <ImageWithFallback
        record={firstImage}
        imgField="webpMdUrl"
        fill
        alt={accommodationRoom.name}
        className="tw-static tw-block tw-rounded-tl-lg tw-rounded-tr-lg tw-object-cover tw-h-52 tw-w-full tw-border-2 tw-border-solid tw-border-b-0 tw-border-brand-green"
      />
      <div className="tw-p-4 tw-border-2 tw-border-t-0 tw-border-brand-green tw-rounded-bl-lg tw-rounded-br-lg">
        <div className="tw-font-semibold tw-font-styleSans tw-text-center tw-mb-1 tw-h-11">
          {truncate(accommodationRoom.name, { length: 40, omission: '...' })}
        </div>
        {option.accommodationSaleType === EnumOptionAccommodationSaleType.entire ? (
          <div className="tw-text-center tw-text-grey tw-text-sm tw-leading-none tw-flex-wrap tw-justify-center tw-h-11">
            {accommodationRoom.beds.length > 0 ? (
              <>
                <span>
                  {accommodationRoom.beds.map((bed) => `${bed.qty} x ${bed.name}`).join(', ')}
                </span>
                <span className="tw-mx-1.5">·</span>
              </>
            ) : null}
            {accommodationRoom.maxNumOfPerson > 0 ? (
              <span>{pluralize('person', accommodationRoom.maxNumOfPerson, true)}</span>
            ) : (
              <div>&nbsp;</div>
            )}
          </div>
        ) : option.accommodationSaleType === 'assigned_room' ? (
          <div className="tw-text-brand-navy tw-text-sm tw-leading-none tw-text-center">
            {accommodationRoom.ordinalNight
              ? `Night ${accommodationRoom.ordinalNight}`
              : isAllNight
              ? 'All Nights'
              : ''}
          </div>
        ) : null}
      </div>
    </div>
  );
};

type Props = {
  isGiftExperience?: boolean;
  option: Option;
  accommodationRooms: AccommodationRoom[];
  orderItemAccommodationRooms?: OrderItemAccommodationRoom[];
  availableDates?: AvailableDate[];
  startDate?: string | null;
};

const OptionAccommodationRoomCarousel: React.FC<Props> = ({
  isGiftExperience,
  option,
  accommodationRooms,
  orderItemAccommodationRooms,
  availableDates,
  startDate,
}) => {
  const [currentSlide, setCurrentSlide] = useState<number>();
  const accommodationRoomSliderRef = useRef<Slider>(null);
  const currentCurrency = useCurrentCurrency();
  const [showLightbox, setShowLightbox] = useState(false);
  const {
    show: showModal,
    setShow: setShowModal,
    viewAccommodationRoomId,
    setViewAccommodationRoomId,
  } = useModalAccommodationRoomContext();
  const viewAccommodationRoom = accommodationRooms.find((o) => o.id === viewAccommodationRoomId);
  const viewAccommodationRoomFirstImage = viewAccommodationRoom?.images[0];
  const sharedSpace = accommodationRooms.find((o) => o.kind === 'shared_space');
  const sortedAccommodationRooms = sortBy(
    flatten(
      accommodationRooms.map((o) => {
        return option.accommodationSaleType === EnumOptionAccommodationSaleType.entire &&
          o.roomType === EnumAccommodationRoomType.dormBed
          ? {
              ...o,
              name: 'Dorm Room',
              maxNumOfPerson: o.qty * o.maxNumOfPerson,
            }
          : option.accommodationSaleType === EnumOptionAccommodationSaleType.entire && o.qty > 1
          ? [...Array(o.qty)].map((_, index) => {
              return {
                ...o,
                name: `${o.name} ${ALPHABET_MAP[index]}`,
              };
            })
          : o;
      })
    ),
    [
      (accommodationRoom) => {
        const sortingValue =
          availableDates && startDate
            ? availableDates.find(
                (o) => o.date === startDate && o.accommodationRoomId === accommodationRoom.id
              )?.capacity || 0
            : undefined;
        return typeof sortingValue === 'number' && sortingValue === 0 ? 9999 : 0;
      },
      'kind',
      'ordinalNight',
      (o) => Number(o.minAmount.amount),
    ]
  );
  const lightboxdAccommodationRooms = sortBy(
    flatten(
      accommodationRooms.map((o) => {
        return option.accommodationSaleType === EnumOptionAccommodationSaleType.entire &&
          o.roomType === EnumAccommodationRoomType.dormBed
          ? {
              ...o,
              name: 'Dorm Room',
              maxNumOfPerson: o.qty * o.maxNumOfPerson,
            }
          : o;
      })
    ),
    ['kind', 'ordinalNight', (o) => Number(o.minAmount.amount)]
  );
  const isAllNight = accommodationRooms.every((o) => typeof o.ordinalNight !== 'number');

  useEffect(() => {
    if (viewAccommodationRoomId) {
      setCurrentSlide(sortedAccommodationRooms.findIndex((o) => o.id === viewAccommodationRoomId));
    }
  }, [viewAccommodationRoomId, option.id]);

  useEffect(() => {
    if (showModal && viewAccommodationRoomId) {
      let accommodationRoomSlide: HTMLElement | null = document.querySelector(
        `#js-accommodation-room-${viewAccommodationRoomId}`
      );
      accommodationRoomSlide = accommodationRoomSlide
        ? accommodationRoomSlide.closest('.slick-slide')
        : null;
      if (accommodationRoomSlide) {
        const slideIndex = accommodationRoomSlide.dataset.index;
        if (slideIndex) {
          if (accommodationRoomSliderRef.current) {
            accommodationRoomSliderRef.current.slickGoTo(Number(slideIndex));
          }
        }
      }
    }
  }, [viewAccommodationRoomId, showModal]);

  return accommodationRooms.length > 0 ? (
    <>
      {option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom ? (
        <div className={styles.slider}>
          <Slider
            slidesToShow={2.3}
            slidesToScroll={1}
            dots={false}
            arrows={true}
            className={`slider-v2 tw--ml-1`}
            swipeToSlide={true}
            infinite={false}
            prevArrow={
              <div className="slick-prev">
                <HiOutlineArrowNarrowLeft
                  size={24}
                  strokeWidth={2}
                  className="tw-text-brand-navy"
                />
              </div>
            }
            nextArrow={
              <div className="slick-next">
                <HiOutlineArrowNarrowRight
                  size={24}
                  strokeWidth={2}
                  className="tw-text-brand-navy"
                />
              </div>
            }
            responsive={[
              {
                breakpoint: 800,
                settings: {
                  slidesToShow: 1.3,
                },
              },
            ]}
          >
            {sortedAccommodationRooms.map((accommodationRoom, index) => (
              <div key={accommodationRoom.id} className="tw-px-1">
                <PickAccommodationRoomCard
                  option={option}
                  accommodationRoom={accommodationRoom}
                  setViewAccommodationRoomId={setViewAccommodationRoomId}
                  setShowModal={setShowModal}
                  qty={
                    availableDates && startDate
                      ? availableDates.find(
                          (o) =>
                            o.date === startDate && o.accommodationRoomId === accommodationRoom.id
                        )?.capacity || 0
                      : undefined
                  }
                />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div className={styles.slider}>
          <Slider
            {...commonCarouselSettings}
            arrows={true}
            slidesToShow={2.5}
            slidesToScroll={2}
            className={`slider-v2 ${styles.sliderCard}`}
            prevArrow={
              <div className="slick-prev">
                <HiOutlineArrowNarrowLeft
                  size={24}
                  strokeWidth={2}
                  className="tw-text-brand-navy"
                />
              </div>
            }
            nextArrow={
              <div className="slick-next">
                <HiOutlineArrowNarrowRight
                  size={24}
                  strokeWidth={2}
                  className="tw-text-brand-navy"
                />
              </div>
            }
            responsive={[
              {
                breakpoint: 800,
                settings: {
                  slidesToShow: 1.5,
                  slidesToScroll: 1,
                },
              },
            ]}
          >
            {sortedAccommodationRooms.map((accommodationRoom, index) => (
              <AccommodationRoomCard
                key={accommodationRoom.id}
                accommodationRoom={accommodationRoom}
                setViewAccommodationRoomId={setViewAccommodationRoomId}
                setShowLightbox={setShowLightbox}
                option={option}
                isAllNight={isAllNight}
              />
            ))}
          </Slider>
        </div>
      )}

      {option.accommodationSaleType === EnumOptionAccommodationSaleType.entire ? (
        <div className="tw-mt-6">
          <div className="tw-font-semibold tw-mb-2">Entire Place</div>
          <div className="tw-mb-2">
            <ImageWithFallback
              src={UserIcon}
              alt=""
              className="tw-static tw-inline-block tw-mr-2 tw-w-6 tw-h-6"
            />
            Sleeps {accommodationRooms.reduce((memo, o) => memo + o.maxNumOfPerson * o.qty, 0)} in
            total
          </div>
          <div className="tw-mb-2">
            <ImageWithFallback
              src={BedIcon}
              alt=""
              className="tw-static tw-inline-block tw-mr-2 tw-w-6 tw-h-6"
            />
            {pluralize(
              'bedroom',
              accommodationRooms
                .filter((o) => o.kind === 'bedroom')
                .reduce(
                  (memo, o) =>
                    memo + (o.roomType === EnumAccommodationRoomType.dormBed ? 1 : o.qty),
                  0
                ),
              true
            )}
          </div>
          <div className="tw-mb-2">
            <ImageWithFallback
              src={ShowerIcon}
              alt=""
              className="tw-static tw-inline-block tw-mr-2 tw-w-6 tw-h-6"
            />
            {pluralize(
              'bathroom',
              accommodationRooms.reduce(
                (memo, o) =>
                  memo +
                  (o.kind === 'bedroom' && o.numPrivateBathroom
                    ? o.qty
                    : o.kind === 'shared_space' && o.numSharedBathroom
                    ? o.numSharedBathroom
                    : 0),
                0
              ),
              true
            )}
          </div>

          {sharedSpace?.amenities && sharedSpace.amenities.length > 0 ? (
            <div className="tw-mt-6">
              <div className="tw-font-semibold tw-mb-2">Shared Spaces</div>
              {sharedSpace.amenities.map((amenity) => (
                <div key={amenity.id} className="tw-inline-block tw-mb-2 tw-mr-2">
                  <CheckInCircleSvg className="tw-w-5 tw-h-5 tw-align-top tw-text-brand-navy tw-mr-1" />
                  <span className="tw-align-top">{amenity.name}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom ? (
        <Modal show={showModal} className="modal-styled tw-z-1300" backdropClassName="tw-z-1300">
          <Modal.Header>
            <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
              <div>
                <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                  {viewAccommodationRoom?.name}
                </div>
              </div>
              <FiX size={24} className="tw-cursor-pointer" onClick={() => setShowModal(false)} />
            </div>
          </Modal.Header>
          <Modal.Body>
            <Slider
              {...commonCarouselSettings}
              arrows={false}
              fade={true}
              ref={accommodationRoomSliderRef}
              className={styles.sliderModal}
              slidesToShow={1}
              slidesToScroll={1}
              beforeChange={(_, currentSlide) => {
                setViewAccommodationRoomId(sortedAccommodationRooms[currentSlide].id);
              }}
            >
              {sortedAccommodationRooms.map((accommodationRoom, index) => {
                const qty =
                  availableDates && startDate
                    ? availableDates.find(
                        (o) =>
                          o.date === startDate && o.accommodationRoomId === accommodationRoom.id
                      )?.capacity || 0
                    : undefined;
                return (
                  <div
                    key={accommodationRoom.id}
                    id={`js-accommodation-room-${accommodationRoom.id}`}
                  >
                    {accommodationRoom.images.length > 0 ? (
                      <Carousel
                        controls={accommodationRoom.images.length > 1}
                        prevIcon={
                          <div className={styles.carouselArrowBtn}>
                            <FiChevronLeft strokeWidth={2} className="tw-text-brand-navy" />
                          </div>
                        }
                        nextIcon={
                          <div className={styles.carouselArrowBtn}>
                            <FiChevronRight strokeWidth={2} className="tw-text-brand-navy" />
                          </div>
                        }
                        indicators={accommodationRoom.images.length > 1}
                        className="carousel-accommodation-room tw-mb-3 carousel slide"
                        interval={null}
                        pause={false}
                        keyboard={false}
                        touch={false}
                      >
                        {accommodationRoom.images.map((image) => (
                          <Carousel.Item key={image.id} className="carousel-item tw-relative">
                            <ImageWithFallback
                              record={image}
                              imgField="webpLgUrl"
                              fill
                              alt={accommodationRoom.name}
                              className="tw-rounded-lg tw-border-brand-green tw-border-2 tw-border-solid tw-object-cover tw-h-80 tw-w-full"
                            />
                          </Carousel.Item>
                        ))}
                      </Carousel>
                    ) : null}

                    <div className="md:tw-flex tw-mt-6">
                      <div className="">
                        <div className="tw-mb-2 tw-flex tw-items-center">
                          <ImageWithFallback
                            src={UserIcon}
                            alt=""
                            className="tw-static tw-inline-block tw-mr-2 tw-w-4 tw-h-4"
                          />
                          {pluralize('person', accommodationRoom.maxNumOfPerson, true)}
                        </div>
                        {accommodationRoom.beds.length > 0 ? (
                          <div className="tw-mb-2 tw-flex tw-items-center">
                            <ImageWithFallback
                              src={BedIcon}
                              alt=""
                              className="tw-static tw-inline-block tw-mr-2 tw-w-4 tw-h-4"
                            />
                            {accommodationRoom.beds
                              .map((bed) => `${bed.qty} x ${bed.name}`)
                              .join(', ')}
                          </div>
                        ) : null}
                        {accommodationRoom.numPrivateBathroom ? (
                          <div className="tw-mb-2 tw-flex tw-items-center">
                            <ImageWithFallback
                              src={ShowerIcon}
                              alt=""
                              className="tw-static tw-inline-block tw-mr-2 tw-w-4 tw-h-4"
                            />
                            <span className="tw-leading-none">
                              {pluralize(
                                'x ensuite bathroom',
                                accommodationRoom.numPrivateBathroom,
                                true
                              )}
                            </span>
                          </div>
                        ) : null}
                        <div className="tw-mb-2">
                          From{' '}
                          <Money
                            hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
                            amount={accommodationRoom.minAmount}
                            toCurrency={currentCurrency}
                            className="tw-font-semibold tw-font-styleSans tw-text-orange"
                          />
                          {displayPriceUnit(
                            option,
                            accommodationRoom.roomType === EnumAccommodationRoomType.dormBed
                              ? '/bed'
                              : undefined
                          )}
                        </div>

                        {typeof qty === 'number' ? (
                          <div className="tw-text-xs tw-leading-none">
                            {qty > 0 ? (
                              <span className="tw-text-orange">
                                {pluralize(
                                  accommodationRoom.roomType === EnumAccommodationRoomType.dormBed
                                    ? 'bed'
                                    : 'room',
                                  qty,
                                  true
                                )}{' '}
                                left on your date
                              </span>
                            ) : (
                              <span className="tw-text-grey">Sold out on your date</span>
                            )}
                          </div>
                        ) : null}
                      </div>
                      <div className="tw-border-t md:tw-border-l tw-border-grey-lighter tw-mx-0 tw-my-6 md:tw-mx-8 md:tw-my-0"></div>
                      <div>
                        {accommodationRoom.amenities.length > 0 ? (
                          <div>
                            <div className="tw-font-semibold tw-mb-2">Room Amenities</div>
                            {accommodationRoom.amenities.map((amenity) => (
                              <div key={amenity.id} className="tw-flex tw-items-center tw-mb-2">
                                <CheckInCircleSvg className="tw-h-5 tw-w-5 tw-text-brand-navy tw-mr-2 tw-flex-shrink-0" />
                                <span className="tw-align-top">{amenity.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </Modal.Body>
          <div
            onClick={() => {
              if (accommodationRoomSliderRef.current) {
                accommodationRoomSliderRef.current.slickPrev();
              }
            }}
            className={classNames(
              'tw-absolute tw-left-2 md:tw--left-14 tw-shadow-md tw-rounded-full tw-w-8 md:tw-w-10 tw-h-8 md:tw-h-10 tw-py-1 md:tw-py-2 tw-bg-white tw-leading-none tw-text-center tw--top-10 md:tw-top-1/2 tw-transform md:tw--translate-y-10 tw-cursor-pointer',
              {
                'tw-opacity-30 tw-cursor-not-allowed':
                  typeof currentSlide === 'number' && currentSlide <= 0,
              }
            )}
          >
            <FiChevronLeft size={24} className="tw-text-brand-navy" />
          </div>
          <div
            onClick={() => {
              if (accommodationRoomSliderRef.current) {
                accommodationRoomSliderRef.current.slickNext();
              }
            }}
            className={classNames(
              'tw-absolute tw-left-12 md:tw-left-auto md:tw--right-14 tw-shadow-md tw-rounded-full tw-w-8 md:tw-w-10 tw-h-8 tw-py-1 md:tw-py-2 md:tw-h-10 tw-bg-white tw-leading-none tw-text-center tw--top-10 md:tw-top-1/2 tw-transform md:tw--translate-y-10 tw-cursor-pointer',
              {
                'tw-opacity-30 tw-cursor-not-allowed':
                  typeof currentSlide === 'number' && currentSlide >= accommodationRooms.length - 1,
              }
            )}
          >
            <FiChevronRight size={24} className="tw-text-brand-navy" />
          </div>
        </Modal>
      ) : (
        <LightBox
          show={showLightbox}
          onClose={() => {
            setViewAccommodationRoomId(undefined);
            setShowLightbox(false);
          }}
          enableNav={true}
          slideId={`js-accommodation-room-img-${viewAccommodationRoomId}-${viewAccommodationRoomFirstImage?.id}`}
          items={flatten(
            lightboxdAccommodationRooms.map((accommodationRoom) => {
              return accommodationRoom.images
                .map((image) => ({
                  id: `js-accommodation-room-img-${accommodationRoom.id}-${image.id}`,
                  imageUrl: image.webpMdUrl,
                  caption: accommodationRoom.name,
                }))
                .filter((o) => !!o.imageUrl);
            })
          )}
        />
      )}
    </>
  ) : null;
};

export default OptionAccommodationRoomCarousel;
